import {
    CoreFetch,
    createRequestFilters,
    FilterMethod,
    ICustomFilter,
    InstitutionalApi,
    ListSupportInstitutionalClaimsOrderEnum,
    SupportCreateInstitutionalClaimTemplatePayload,
    SupportInstitutionalClaimTemplate,
    SupportInstitutionalTemplateServiceLine,
    SupportInstitutionalTemplateSortType,
    SupportPatchInstitutionalClaimTemplatePayload,
    SupportUpdateInstitutionalClaimTemplatePayload,
} from '@enter/core';
import _ from 'lodash';
import { action, runInAction } from 'mobx';
import { IFetchResourceRequest } from '../services/store/store.service';
import { IResourceStore, ResourceStore } from './resource.store';

export class InstitutionalTemplatesStore
    extends ResourceStore<SupportInstitutionalClaimTemplate>
    implements IResourceStore<SupportInstitutionalClaimTemplate> {
    public static getDisplayName(institutional: SupportInstitutionalClaimTemplate): string {
        return institutional.name;
    }

    public static getId(institutional: SupportInstitutionalClaimTemplate): string {
        return institutional.id || '';
    }

    public static getLineDisplayName(serviceLine: SupportInstitutionalTemplateServiceLine): string {
        const procedure = serviceLine.f44ProcedureCode && `Procedure: ${serviceLine.f44ProcedureCode}`;
        const amount =
            serviceLine.f47TotalChargeAmount &&
            `${serviceLine.f47TotalChargeAmount.currency} ${serviceLine.f47TotalChargeAmount.amount}`;

        return _.compact([procedure, amount]).join(', ');
    }

    public static async getByQuery(query: string): Promise<SupportInstitutionalClaimTemplate[]> {
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterName',
                filterMethod: FilterMethod.LK,
                filterType: 'String',
                value: query,
            },
        ];

        const reqFilters = createRequestFilters(filters);
        return CoreFetch<InstitutionalApi>(InstitutionalApi).listSupportInstitutionalClaimTemplates({
            ...reqFilters,
        });
    }

    public static async getByClaimId(claimId: string): Promise<SupportInstitutionalClaimTemplate | undefined> {
        const filters: ICustomFilter[] = [
            {
                filterId: 'filterClaimId',
                filterMethod: FilterMethod.EQ,
                filterType: 'String',
                value: claimId,
            },
        ];
        const reqFilters = createRequestFilters(filters);

        const institutionalClaims = await CoreFetch<InstitutionalApi>(InstitutionalApi).listSupportInstitutionalClaims({
            ...reqFilters,
        });

        const institutionalClaim = _.first(institutionalClaims);

        if (!institutionalClaim?.institutionalTemplateId) {
            return;
        }

        return CoreFetch<InstitutionalApi>(InstitutionalApi).getSupportInstitutionalClaimTemplate({
            id: institutionalClaim.institutionalTemplateId,
        });
    }

    public static async getOne(id: string): Promise<SupportInstitutionalClaimTemplate> {
        return CoreFetch<InstitutionalApi>(InstitutionalApi).getSupportInstitutionalClaimTemplate({
            id,
        });
    }

    public static edit = async (id: string, payload: SupportUpdateInstitutionalClaimTemplatePayload): Promise<void> => {
        await CoreFetch<InstitutionalApi>(InstitutionalApi).updateSupportInstitutionalClaimTemplate({
            id,
            supportUpdateInstitutionalClaimTemplatePayload: payload,
        });
    };

    public static patch = async (id: string, payload: SupportPatchInstitutionalClaimTemplatePayload): Promise<void> => {
        await CoreFetch<InstitutionalApi>(InstitutionalApi).patchSupportInstitutionalClaimTemplate({
            id,
            supportPatchInstitutionalClaimTemplatePayload: payload,
        });
    };

    public fetch = async (req?: IFetchResourceRequest): Promise<void> => {
        runInAction(() => {

            this.loading = true;

        });

        const reqOptions = this.handleCreateRequestOptions(
            req,
            SupportInstitutionalTemplateSortType,
            ListSupportInstitutionalClaimsOrderEnum,
        );
        const raw = await CoreFetch<InstitutionalApi>(InstitutionalApi).listSupportInstitutionalClaimTemplatesRaw(
            reqOptions,
        );

        await this.handleFetchResource(raw);

        runInAction(() => {


            this.loading = false;


        });
    };

    public create = async (payload: SupportCreateInstitutionalClaimTemplatePayload): Promise<void> => {
        runInAction(() => {

            this.loading = true;

        });
        await CoreFetch<InstitutionalApi>(InstitutionalApi).createSupportInstitutionalClaimTemplate({
            supportCreateInstitutionalClaimTemplatePayload: payload,
        });
        runInAction(() => {

            this.loading = false;

        });
    };

    public edit = async (
        id: string,
        payload: SupportUpdateInstitutionalClaimTemplatePayload,
    ): Promise<void> => {
        runInAction(() => {

            this.loading = true;

        });
        await CoreFetch<InstitutionalApi>(InstitutionalApi).updateSupportInstitutionalClaimTemplate({
            id,
            supportUpdateInstitutionalClaimTemplatePayload: payload,
        });
        runInAction(() => {

            this.loading = false;

        });
    };

    public patch = async (
        id: string,
        payload: SupportPatchInstitutionalClaimTemplatePayload,
    ): Promise<void> => {
        runInAction(() => {

            this.loading = true;

        });
        await CoreFetch<InstitutionalApi>(InstitutionalApi).patchSupportInstitutionalClaimTemplate({
            id,
            supportPatchInstitutionalClaimTemplatePayload: payload,
        });
        runInAction(() => {

            this.loading = false;

        });
    };

    public delete = async (id: string): Promise<void> => {
        runInAction(() => {

            this.loading = true;

        });
        await CoreFetch<InstitutionalApi>(InstitutionalApi).disableSupportInstitutionalClaim({
            id,
        });
        runInAction(() => {

            this.loading = false;

        });
    };
}

export default new InstitutionalTemplatesStore();
